import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  TablePagination,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  WhatsApp,
  Search,
  Refresh,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import { useTranslation } from "react-i18next";
import { i18n as i18nInstance } from "../../translate/i18n";

const Leads = () => {
  const { user } = useContext(AuthContext);
  const { t, i18n } = useTranslation(undefined, { i18n: i18nInstance });
  const companyId = user?.companyId;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    qualified: 0,
    contacted: 0,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    source: "other",
    status: "new",
    score: 0,
    notes: "",
  });

  const statusColors = {
    new: "#6366f1",
    contacted: "#f59e0b",
    qualified: "#10b981",
    converted: "#22c55e",
    lost: "#ef4444",
  };

  const statusLabels = useMemo(() => ({
    new: t("leads.status.new"),
    contacted: t("leads.status.contacted"),
    qualified: t("leads.status.qualified"),
    converted: t("leads.status.converted"),
    lost: t("leads.status.lost"),
  }), [t]);

  const sourceLabels = useMemo(() => ({
    whatsapp: t("leads.sources.whatsapp"),
    facebook: t("leads.sources.facebook"),
    instagram: t("leads.sources.instagram"),
    tiktok: t("leads.sources.tiktok"),
    website: t("leads.sources.website"),
    referral: t("leads.sources.referral"),
    cold_call: t("leads.sources.cold_call"),
    email: t("leads.sources.email"),
    other: t("leads.sources.other"),
  }), [t]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      if (!companyId) {
        setLeads([]);
        setTotalCount(0);
        setHasMore(false);
        setStats({ total: 0, new: 0, qualified: 0, contacted: 0 });
        setLoading(false);
        return;
      }

      const params = {
        company_id: companyId,
        // Keep both param styles for backward compatibility across backend routes.
        page: page + 1,
        page_size: rowsPerPage,
        pageNumber: page + 1,
        limit: rowsPerPage,
      };

      if (searchParam) params.searchParam = searchParam;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;

      const { data } = await api.get("/leads", { params });

      const leadsData = data.items || data.leads || [];
      const total = data.total || data.count || 0;

      setLeads(leadsData);
      setTotalCount(total);
      setHasMore(Boolean(data.hasMore) || leadsData.length === rowsPerPage);

      // Calculate stats from leads
      setStats({
        total,
        new: leadsData.filter((l) => l.status === "new").length,
        qualified: leadsData.filter((l) => l.status === "qualified").length,
        contacted: leadsData.filter((l) => l.status === "contacted").length,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error(i18nInstance.t("leads.toasts.loadError"));
      setLeads([]);
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, searchParam, statusFilter, sourceFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleOpenDialog = (lead = null) => {
    if (lead) {
      setEditingLead(lead);
      setFormData({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        source: lead.source || "other",
        status: lead.status || "new",
        score: lead.score || 0,
        notes: lead.notes || "",
      });
    } else {
      setEditingLead(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        source: "other",
        status: "new",
        score: 0,
        notes: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLead(null);
  };

  const handleSaveLead = async () => {
    try {
      if (!formData.name) {
        toast.error(t("leads.form.nameRequired"));
        return;
      }

      if (editingLead) {
        await api.put(`/leads/${editingLead.id}`, formData);
        toast.success(t("leads.toasts.updated"));
      } else {
        const payload = {
          ...formData,
          companyId: user?.companyId,
        };
        await api.post("/leads", payload);
        toast.success(t("leads.toasts.created"));
      }
      handleCloseDialog();
      fetchLeads();
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error(t("leads.toasts.saveError"));
    }
  };

  const handleDeleteLead = async (id) => {
    if (window.confirm(t("leads.confirm.delete"))) {
      try {
        await api.delete(`/leads/${id}`);
        toast.success(t("leads.toasts.deleted"));
        fetchLeads();
      } catch (error) {
        console.error("Error deleting lead:", error);
        toast.error(t("leads.toasts.deleteError"));
      }
    }
  };

  const handleContactLead = (lead, method) => {
    if (method === "whatsapp" && lead.phone) {
      window.open(`https://wa.me/${lead.phone.replace(/[^0-9]/g, "")}`, "_blank");
    } else if (method === "email" && lead.email) {
      window.location.href = `mailto:${lead.email}`;
    } else if (method === "phone" && lead.phone) {
      window.location.href = `tel:${lead.phone}`;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearchParam(e.target.value);
    setPage(0);
  };

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom data-testid="leads-title">
            {t("leads.title")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t("leads.subtitle")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchLeads}
            disabled={loading}
          >
            {t("leads.buttons.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            data-testid="leads-new-button"
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
            }}
          >
            {t("leads.buttons.new")}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: "4px solid #6366f1" }}>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {t("leads.stats.total")}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#6366f1">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: "4px solid #f59e0b" }}>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {t("leads.stats.new")}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#f59e0b">
                {stats.new}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: "4px solid #10b981" }}>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {t("leads.stats.qualified")}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#10b981">
                {stats.qualified}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: "4px solid #ec4899" }}>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {t("leads.stats.contacted")}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#ec4899">
                {stats.contacted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          placeholder={t("leads.filters.searchPlaceholder")}
          value={searchParam}
          onChange={handleSearch}
          size="small"
          sx={{ minWidth: 250 }}
          inputProps={{ "data-testid": "leads-search" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("leads.filters.status")}</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label={t("leads.filters.status")}
          >
            <MenuItem value="">{t("leads.filters.all")}</MenuItem>
            <MenuItem value="new">{t("leads.status.new")}</MenuItem>
            <MenuItem value="contacted">{t("leads.status.contacted")}</MenuItem>
            <MenuItem value="qualified">{t("leads.status.qualified")}</MenuItem>
            <MenuItem value="converted">{t("leads.status.converted")}</MenuItem>
            <MenuItem value="lost">{t("leads.status.lost")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("leads.filters.source")}</InputLabel>
          <Select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(0);
            }}
            label={t("leads.filters.source")}
          >
            <MenuItem value="">{t("leads.filters.allFeminine")}</MenuItem>
            <MenuItem value="whatsapp">{t("leads.sources.whatsapp")}</MenuItem>
            <MenuItem value="facebook">{t("leads.sources.facebook")}</MenuItem>
            <MenuItem value="instagram">{t("leads.sources.instagram")}</MenuItem>
            <MenuItem value="website">{t("leads.sources.website")}</MenuItem>
            <MenuItem value="referral">{t("leads.sources.referral")}</MenuItem>
            <MenuItem value="other">{t("leads.sources.other")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Leads Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table data-testid="leads-table">
              <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell>
                    <strong>{t("leads.table.name")}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t("leads.table.contact")}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t("leads.table.status")}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t("leads.table.source")}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t("leads.table.score")}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t("leads.table.date")}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{t("leads.table.actions")}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary" sx={{ py: 4 }}>
                        {t("leads.table.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {lead.name}
                        </Typography>
                        {lead.email && (
                          <Typography variant="caption" color="textSecondary">
                            {lead.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {lead.phone && (
                            <>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleContactLead(lead, "whatsapp")}
                                title={t("leads.contactActions.whatsapp")}
                              >
                                <WhatsApp fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleContactLead(lead, "phone")}
                                title={t("leads.contactActions.call")}
                              >
                                <Phone fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {lead.email && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleContactLead(lead, "email")}
                              title={t("leads.contactActions.email")}
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[lead.status] || lead.status}
                          size="small"
                          sx={{
                            backgroundColor: statusColors[lead.status] || "#9ca3af",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {sourceLabels[lead.source] || lead.source}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.score || 0}
                          size="small"
                          color={lead.score >= 70 ? "success" : lead.score >= 40 ? "warning" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString(i18n.language || undefined) : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(lead)}
                          data-testid="lead-edit"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLead(lead.id)}
                          data-testid="lead-delete"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage={t("leads.pagination.rowsPerPage")}
              labelDisplayedRows={({ from, to, count }) => {
                const countLabel = count !== -1 ? count : t("leads.pagination.moreThan", { to });
                return t("leads.pagination.displayedRows", { from, to, count: countLabel });
              }}
            />
          </>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLead ? t("leads.dialog.editTitle") : t("leads.dialog.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${t("leads.form.name")} *`}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                inputProps={{ "data-testid": "lead-name" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("leads.form.email")}
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                inputProps={{ "data-testid": "lead-email" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("leads.form.phone")}
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder={t("leads.form.phonePlaceholder")}
                inputProps={{ "data-testid": "lead-phone" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("leads.form.status")}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  label={t("leads.form.status")}
                  inputProps={{ "data-testid": "lead-status" }}
                >
                  <MenuItem value="new">{t("leads.status.new")}</MenuItem>
                  <MenuItem value="contacted">{t("leads.status.contacted")}</MenuItem>
                  <MenuItem value="qualified">{t("leads.status.qualified")}</MenuItem>
                  <MenuItem value="converted">{t("leads.status.converted")}</MenuItem>
                  <MenuItem value="lost">{t("leads.status.lost")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("leads.form.source")}</InputLabel>
                <Select
                  value={formData.source}
                  onChange={(e) =>
                    setFormData({ ...formData, source: e.target.value })
                  }
                  label={t("leads.form.source")}
                  inputProps={{ "data-testid": "lead-source" }}
                >
                  <MenuItem value="whatsapp">{t("leads.sources.whatsapp")}</MenuItem>
                  <MenuItem value="facebook">{t("leads.sources.facebook")}</MenuItem>
                  <MenuItem value="instagram">{t("leads.sources.instagram")}</MenuItem>
                  <MenuItem value="tiktok">{t("leads.sources.tiktok")}</MenuItem>
                  <MenuItem value="website">{t("leads.sources.website")}</MenuItem>
                  <MenuItem value="referral">{t("leads.sources.referral")}</MenuItem>
                  <MenuItem value="cold_call">{t("leads.sources.cold_call")}</MenuItem>
                  <MenuItem value="email">{t("leads.sources.email")}</MenuItem>
                  <MenuItem value="other">{t("leads.sources.other")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("leads.form.score")}
                type="number"
                value={formData.score}
                onChange={(e) =>
                  setFormData({ ...formData, score: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })
                }
                inputProps={{ min: 0, max: 100, "data-testid": "lead-score" }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("leads.form.notes")}
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                inputProps={{ "data-testid": "lead-notes" }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("leads.buttons.cancel")}</Button>
          <Button
            onClick={handleSaveLead}
            variant="contained"
            data-testid="lead-save"
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            }}
          >
            {editingLead ? t("leads.buttons.update") : t("leads.buttons.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Leads;
